import { useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { CaseTable, CasesFailure, loadCases, readFilters } from "../components/register/CaseList";
import { STATES, stateLabel } from "../components/register/caseFlow";
import { Loading } from "../components/States";
import { docs } from "../lib/api";
import { useMe } from "../lib/me";

/** /cases. Every obsolescence case this person may open, newest first. Filters live in the URL. */
export function Cases() {
  const [params, setParams] = useSearchParams();
  const filters = readFilters(params);
  const { me } = useMe();
  const cases = useQuery({ queryKey: ["cases", filters.state, filters.package], queryFn: () => loadCases(filters) });
  // A scoped person picks from their own packages; everyone else from the packages on disk.
  const onDisk = useQuery({ queryKey: ["packages"], queryFn: docs.packages, enabled: me?.packages === null });
  const packages = me?.packages ?? (onDisk.data ?? []).map((p) => p.name);

  const set = (key: "state" | "package", value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value); else next.delete(key);
    setParams(next, { replace: true });
  };

  return (
    <div className="page">
      <h2 style={{ fontSize: 26, marginBottom: 6 }}>Cases</h2>
      <p className="muted" style={{ marginBottom: 20 }}>
        What was decided about each obsolete or announced part, and who decided it. Newest first.
      </p>

      <div className="filters">
        <label className="row" style={{ gap: 6 }}>
          <span className="muted">State</span>
          <select value={filters.state ?? ""} onChange={(e) => set("state", e.target.value)}>
            <option value="">All states</option>
            {STATES.map((s) => <option key={s} value={s}>{stateLabel(s)}</option>)}
          </select>
        </label>
        <label className="row" style={{ gap: 6 }}>
          <span className="muted">Package</span>
          <select value={filters.package ?? ""} onChange={(e) => set("package", e.target.value)}>
            <option value="">All packages</option>
            {filters.package && !packages.includes(filters.package) && <option value={filters.package}>{filters.package}</option>}
            {packages.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        </label>
        {cases.data && <span className="muted" style={{ marginLeft: "auto" }}>{cases.data.length} shown</span>}
      </div>

      {cases.isPending && <Loading what="Loading cases" />}
      {cases.isError && <CasesFailure error={cases.error} onRetry={() => void cases.refetch()} />}
      {cases.data && <CaseTable cases={cases.data} filtered={!!(filters.state || filters.package)} />}
    </div>
  );
}
